<template>
  <section
    :class="[
      props.frameless
        ? 'bg-transparent'
        : 'bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'
    ]"
  >
    <div :class="props.frameless ? 'pt-1 pb-2' : 'px-6 pt-5 pb-4 border-b border-gray-200'">
      <h2 class="mb-0 text-xl font-semibold leading-tight text-black">
        Répartition clients par pays
      </h2>
      <p class="mb-0 mt-2 text-sm leading-tight text-gray-500">{{ periodLabel }}</p>
    </div>

    <div :class="props.frameless ? 'pt-4 pb-3' : 'px-6 py-5'">
      <div v-if="loading" class="flex h-80 items-center justify-center">
        <div class="flex flex-col items-center gap-3">
          <div class="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
          <span class="text-sm font-medium text-gray-500">Chargement de la répartition...</span>
        </div>
      </div>

      <div v-else-if="groupedRows.length === 0" class="flex h-64 items-center justify-center text-sm text-gray-500">
        Aucune donnée client sur cette période.
      </div>

      <div v-else>
        <div class="relative mx-auto h-80 max-w-4xl">
          <Pie :data="chartData" :options="chartOptions" />

          <svg
            v-if="selectedCountry && arrowLine"
            class="country-arrow absolute inset-0 z-10 h-full w-full pointer-events-none"
            aria-hidden="true"
          >
            <defs>
              <marker
                id="country-arrow-head"
                markerWidth="8"
                markerHeight="8"
                refX="7"
                refY="4"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M 0 0 L 8 4 L 0 8 z" :fill="arrowLine.color" />
              </marker>
            </defs>
            <line
              :x1="arrowLine.x1"
              :y1="arrowLine.y1"
              :x2="arrowLine.x2"
              :y2="arrowLine.y2"
              :stroke="arrowLine.color"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-dasharray="5 5"
              marker-end="url(#country-arrow-head)"
            />
          </svg>

          <div
            v-if="selectedCountry"
            class="country-popover absolute z-20 w-[292px] rounded-lg border border-gray-200 bg-white p-3"
          >
            <div class="mb-3 flex items-start justify-between gap-3">
              <div>
                <div class="text-sm font-semibold text-black">{{ selectedCountry.pays }}</div>
                <div class="text-xs text-gray-500">
                  Top produits vendus · {{ formatPercent(selectedCountry.clients) }}
                </div>
              </div>
              <button
                type="button"
                class="flex h-6 w-6 items-center justify-center rounded-full text-sm text-gray-400 hover:bg-gray-100 hover:text-black"
                @click="closePopover"
              >
                ×
              </button>
            </div>

            <div v-if="isLoadingProducts" class="space-y-3">
              <div v-for="index in 3" :key="index" class="flex animate-pulse items-center gap-3">
                <div class="h-10 w-10 rounded bg-gray-200"></div>
                <div class="min-w-0 flex-1">
                  <div class="mb-2 h-3 w-2/3 rounded bg-gray-200"></div>
                  <div class="h-2.5 w-1/2 rounded bg-gray-100"></div>
                </div>
              </div>
            </div>

            <div v-else-if="countryProducts.length === 0" class="py-5 text-center text-sm text-gray-500">
              Aucun produit trouvé.
            </div>

            <div v-else class="space-y-3">
              <article
                v-for="product in countryProducts"
                :key="product.id_produit"
                class="flex items-center gap-3"
              >
                <div class="h-10 w-10 shrink-0 overflow-hidden rounded bg-gray-100 ring-1 ring-gray-200">
                  <img
                    v-if="product.imageUrl && !failedImages[product.id_produit]"
                    :src="product.imageUrl"
                    :alt="product.reference || 'Produit'"
                    class="h-full w-full object-cover"
                    @error="failedImages[product.id_produit] = true"
                  />
                  <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-gray-500">
                    {{ getInitial(product.reference) }}
                  </div>
                </div>

                <div class="min-w-0 flex-1">
                  <div class="truncate text-sm font-medium text-black">{{ product.reference || 'Produit' }}</div>
                  <div class="truncate text-xs text-gray-500">{{ product.categorie || 'Sans catégorie' }}</div>
                </div>

                <div class="text-right">
                  <div class="text-sm font-semibold text-black">{{ product.quantite }}</div>
                  <div class="text-xs text-gray-500">{{ formatCurrency(product.chiffre_affaires) }}</div>
                </div>
              </article>
            </div>
          </div>
        </div>

        <div class="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2">
          <div
            v-for="(row, index) in groupedRows"
            :key="row.pays"
            class="inline-flex items-center gap-2 text-sm text-black"
          >
            <span
              class="h-3 w-6 rounded-sm"
              :style="{ backgroundColor: colors[index % colors.length] }"
            ></span>
            <span>{{ row.pays }} {{ formatPercent(row.clients) }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js'
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js'
import type { RepartitionClientsPaysApi } from '@/types'
import { CommandesPrestationsApi } from '@/api/commandesPrestations'

ChartJS.register(ArcElement, Tooltip, Legend)

const props = withDefaults(defineProps<{
  rows: RepartitionClientsPaysApi[]
  loading?: boolean
  periodLabel: string
  frameless?: boolean
  sellerId?: number | null
  range?: string
}>(), {
  rows: () => [],
  loading: false,
  frameless: false,
  sellerId: null,
  range: 'mois',
})

type CountryProduct = {
  id_produit: number
  reference: string | null
  imageUrl?: string | null
  categorie?: string | null
  quantite: number
  chiffre_affaires: number
}

type GroupedCountry = RepartitionClientsPaysApi & {
  countryIds?: number[]
}

const selectedCountry = ref<GroupedCountry | null>(null)
const countryProducts = ref<CountryProduct[]>([])
const isLoadingProducts = ref(false)
const failedImages = reactive<Record<number, boolean>>({})
const arrowLine = ref<{
  x1: number
  y1: number
  x2: number
  y2: number
  color: string
} | null>(null)

const colors = [
  '#e11d48',
  '#0f2a66',
  '#f0ddb7',
  '#b8cfc6',
  '#c8b58b',
  '#9fd3bd',
  '#f1d38a',
  '#cf758c',
  '#dfc28a',
]

const ARROW_COLOR = '#64748b'

const totalClients = computed(() =>
  props.rows.reduce((sum, row) => sum + Number(row.clients || 0), 0),
)

const groupedRows = computed<GroupedCountry[]>(() => {
  const cleaned = props.rows
    .map((row) => ({
      ...row,
      pays: row.pays || 'Non renseigné',
      clients: Number(row.clients || 0),
    }))
    .filter((row) => row.clients > 0)
    .sort((a, b) => b.clients - a.clients)

  if (cleaned.length <= 8) return cleaned

  const visible = cleaned.slice(0, 8)
  const hiddenRows = cleaned.slice(8)
  const autres = hiddenRows.reduce((sum, row) => sum + row.clients, 0)
  const countryIds = hiddenRows
    .map((row) => row.id_pays)
    .filter((id): id is number => typeof id === 'number' && id > 0)
  if (autres > 0) {
    visible.push({
      id_pays: null,
      pays: 'Autres',
      clients: autres,
      countryIds,
    })
  }
  return visible
})

const formatPercent = (value: number) => {
  if (!totalClients.value) return '0,00%'
  const percent = (Number(value || 0) / totalClients.value) * 100
  return `${percent.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`
}

const formatCurrency = (value: number) =>
  `${Number(value || 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`

const getInitial = (reference: string | null) =>
  (reference || '?').trim().charAt(0).toUpperCase() || '?'

const closePopover = () => {
  selectedCountry.value = null
  countryProducts.value = []
  isLoadingProducts.value = false
  arrowLine.value = null
}

const loadCountryProducts = async (country: GroupedCountry) => {
  const countryIds = country.countryIds ?? []
  if (!props.sellerId || (country.id_pays == null && countryIds.length === 0)) return

  isLoadingProducts.value = true
  countryProducts.value = []
  try {
    countryProducts.value = await CommandesPrestationsApi.getBestReferencesByCountry(
      props.sellerId,
      country.id_pays ?? null,
      props.range,
      5,
      countryIds,
    )
  } catch {
    countryProducts.value = []
  } finally {
    isLoadingProducts.value = false
  }
}

const handleSegmentClick = async (event: any, elements: any[], chart: any) => {
  if (!elements.length) {
    closePopover()
    return
  }

  const index = elements[0]?.index
  const country = groupedRows.value[index]
  const countryIds = country?.countryIds ?? []
  if (!country || !props.sellerId || (country.id_pays == null && countryIds.length === 0)) {
    closePopover()
    return
  }

  const element = elements[0]?.element
  const position = element?.tooltipPosition?.() ?? element
  const startX = typeof position?.x === 'number' ? position.x : chart.width / 2
  const startY = typeof position?.y === 'number' ? position.y : chart.height / 2
  const popoverLeft = chart.width - 4 - 292
  const popoverTop = -8

  arrowLine.value = {
    x1: startX,
    y1: startY,
    x2: popoverLeft - 8,
    y2: popoverTop + 36,
    color: ARROW_COLOR,
  }
  selectedCountry.value = country
  await loadCountryProducts(country)
}

const chartData = computed<ChartData<'pie'>>(() => ({
  labels: groupedRows.value.map((row) => `${row.pays}: ${formatPercent(row.clients)}`),
  datasets: [
    {
      data: groupedRows.value.map((row) => row.clients),
      backgroundColor: groupedRows.value.map((_, index) => colors[index % colors.length]),
      borderColor: '#ffffff',
      borderWidth: 2,
      hoverOffset: 18,
      offset: groupedRows.value.map((_, index) => (index === 0 ? 14 : 0)),
    },
  ],
}))

const chartOptions = computed<ChartOptions<'pie'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 16,
      right: 24,
      bottom: 8,
      left: 24,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      padding: 10,
      callbacks: {
        label(context: TooltipItem<'pie'>) {
          const value = Number(context.parsed || 0)
          return `${context.label}: ${value} client${value > 1 ? 's' : ''}`
        },
      },
    },
  },
  onClick: handleSegmentClick,
  onHover(event: any, elements: any[], chart: any) {
    if (chart?.canvas) {
      chart.canvas.style.cursor = elements.length ? 'pointer' : 'default'
    }
  },
}))

watch(
  () => [props.rows, props.range, props.sellerId],
  () => closePopover(),
)
</script>

<style scoped>
.country-popover {
  top: -8px;
  right: 4px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
}

@media (max-width: 768px) {
  .country-popover {
    top: 32px;
    right: 50%;
    width: min(292px, calc(100% - 24px));
    transform: translateX(50%);
  }

  .country-arrow {
    display: none;
  }
}
</style>
